import {useEffect, useMemo, useRef, useState} from "react";
import {
  Button,
  Card,
  Col,
  Collapse,
  Container,
  Form,
  Row,
  Spinner
} from "react-bootstrap";
import DefaultLayout from "../layouts/DefaultLayout";
import useTranslation from "../hooks/translations";
import {TrendingDown} from "react-feather";
import MonthNavigator from "../components/MonthNavigator";
import {useApi} from "../contexts/ApiProvider";
import CostService from "../services/CostService";
import SimpleDataTable from "../components/SimpleDataTable";
import {useDateRange} from "../contexts/DateRangeProvider";
import CostModal from "./modals/CostModal";
import ConfirmationModal from "./modals/ConfirmationModal";
import DatesUtils from "../utils/dates";
import StringUtils from "../utils/StringUtils";
import CurrencyUtils from "../utils/CurrencyUtils";
import FileSaver from 'file-saver';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import SelectField from "../components/SelectField";
import CostTypeService from "../services/CostTypeService";
import CostCategoryService from "../services/CostCategoryService";
import DateRangeField from "../components/DateRangeField";
import ProtectedContent from "../components/ProtectedContent";

export default function CostsPage() {
  const translations = useTranslation();
  const {from, to} = useDateRange();

  const pageTitle = translations.costs;
  const pageSubtitle = translations.costsPageSubtitle;

  const api = useApi();
  const [isLoading, setIsLoading] = useState(false);
  const costService = useMemo(() => new CostService(api), [api]);
  const costTypeService = useMemo(() => new CostTypeService(api), [api]);
  const costCategoryService = useMemo(() => new CostCategoryService(api), [api]);

  const columns = [
    {
      name: translations.date,
      data_identifier: "dateFormatted"
    },
    {
      name: translations.amount,
      data_identifier: "amountFormatted"
    },
    {
      name: translations.name,
      data_identifier: "name"
    },
    {
      name: translations.description,
      data_identifier: "description"
    },
    {
      name: translations.costType,
      data_identifier: "type"
    },
    {
      name: translations.costCategory,
      data_identifier: "category"
    }
  ];

  const [rows, setRows] = useState([]);
  const [selectedRow, setSelectedRow] = useState({});

  const [openCostModal, setOpenCostModal] = useState(false);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);

  const [openFilters, setOpenFilters] = useState(false);

  const [costTypeOptions, setCostTypeOptions] = useState([]);
  const [selectedCostTypeOption, setSelectedCostTypeOption] = useState("");
  const handleCostTypeOptionChange = (option) => {
    setSelectedCostTypeOption(option.currentTarget.value);
  };

  const [costCategoryOptions, setCostCategoryOptions] = useState([]);
  const [filteredCostCategoryOptions, setFilteredCostCategoryOptions] = useState([]);
  const [selectedCostCategoryOption, setSelectedCostCategoryOption] = useState("");
  const handleCostCategoryOptionChange = (option) => {
    setSelectedCostCategoryOption(option.currentTarget.value);
  };

  const [dateFrom, setDateFrom] = useState(from);
  const [dateTo, setDateTo] = useState(to);

  const dateFromField = useRef();
  const dateToField = useRef();
  const typeField = useRef();
  const categoryField = useRef();
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    (async () => {
      setDateFrom(from);
      setDateTo(to);

      await getData(from, to, selectedCostTypeOption, selectedCostCategoryOption);
    })();
  }, [from, to]);

  useEffect(() => {
    const getCostTypes = async () => {
      const response = await costTypeService.getAll();
      const typeOptions = response.body.map((item) => {
        return {
          value: item.id,
          name: item.name
        };
      });
      setCostTypeOptions(typeOptions);
    }
    (async () => {
      await getCostTypes();
    })();
  }, [costTypeService]);

  useEffect(() => {
    const getCostCategories = async () => {
      const response = await costCategoryService.getAll();
      const categoryOptions = response.body.map((item) => {
        return {
          value: item.id,
          name: item.name,
          type: item.type.id
        };
      });
      setCostCategoryOptions(categoryOptions);
      setFilteredCostCategoryOptions(categoryOptions);
    }
    (async () => {
      await getCostCategories();
    })();
  }, [costCategoryService]);

  useEffect(() => {
    if (!selectedCostTypeOption) {
      setFilteredCostCategoryOptions(costCategoryOptions);
      setSelectedCostCategoryOption("");
      return;
    }

    setFilteredCostCategoryOptions(costCategoryOptions.filter(
      (option) => option.type === Number(selectedCostTypeOption))
    );
  }, [selectedCostTypeOption]);

  const getData = async (start, end, typeId = undefined, categoryId = undefined) => {
    setIsLoading(true);
    const response = await costService.search(start, end, typeId, categoryId);
    const formatedResponse = response.body.map((row) => {
      const dateObject = new Date(row.timestamp);
      const currencyFormatter = CurrencyUtils.getCurrencyFormatter(
        row.currency.symbol
      );
      return {
        id: row.id,
        name: row.name,
        description: row.description,
        type: row.type.name,
        typeId: row.type.id,
        category: row.category.name,
        categoryId: row.category.id,
        amountFormatted: currencyFormatter.format(row.amount),
        amount: row.amount,
        date: DatesUtils.formatDate(dateObject),
        dateFormatted: DatesUtils.format(dateObject),
        currencyId: row.currency.id,
        currencySymbol: row.currency.symbol
      }
    });
    setRows(formatedResponse);
    setIsLoading(false);
  }

  // datatable callbacks
  const onEditClick = async (data) => {
    setSelectedRow(data)
    setOpenCostModal(true)
  }

  const onDeleteClick = async (data) => {
    setSelectedRow(data)
    setOpenConfirmationModal(true)
  }

  const onAddClick = async () => {
    setSelectedRow({})
    setOpenCostModal(true)
  }

  const onExportClick = async () => {
    const csvResponse = await costService.export(from, to, selectedCostTypeOption, selectedCostCategoryOption);
    if (!csvResponse.ok) {
      return
    }
    const csvData = new Blob([csvResponse.body], {type: 'text/csv;charset=utf-8;'});
    const filename = csvResponse.rawResponse.headers.get('Content-Disposition').split('filename=')[1];
    FileSaver.saveAs(csvData, filename ? filename : "csv_export");
  }

  // cost modal callbacks
  const onModalClose = async () => {
    setOpenCostModal(false);
  }
  const onModalSubmit = async (data) => {
    await getData(from, to, selectedCostTypeOption, selectedCostCategoryOption);
    setOpenCostModal(false);
  }

  // confirmation modal callbacks
  const onConfirmationModalSubmit = async (data) => {
    await costService.delete(data.id)
    await getData(from, to, selectedCostTypeOption, selectedCostCategoryOption);
    setOpenConfirmationModal(false)
  }

  const onConfirmationModalClose = async () => {
    setOpenConfirmationModal(false)
  }

  const onSearch = async (event) => {
    event.preventDefault();

    const from = new Date(dateFromField.current.value);
    const to = new Date(dateToField.current.value);

    setDateFrom(from);
    setDateTo(to);

    await getData(from, to, selectedCostTypeOption, selectedCostCategoryOption);
  }

  const onClearSearch = async (event) => {
    setDateFrom(from);
    setDateTo(to);
    setSelectedCostTypeOption("");
    setSelectedCostCategoryOption("");

    await getData(from, to, "", "");
  }

  const totalCosts = useMemo(
    () => {
      const total = rows.reduce((accumulatedCosts, currentCost) => {
        return accumulatedCosts + currentCost.amount
      }, 0);

      const currencyFormatter = CurrencyUtils.getCurrencyFormatter(
        !rows || rows.length === 0 ? undefined : rows[0].currencySymbol
      );

      return currencyFormatter.format(total);
    }, [rows]);

  return (
    <DefaultLayout icon={<TrendingDown/>}
                   title={pageTitle}
                   subtitle={pageSubtitle}>
      <Container fluid="xl" className="px-4 mt-n10">
        <Card className="mb-4">
          <Card.Header>
            <Container fluid>
              <Row>
                <Col style={{paddingLeft: 0, paddingRight: 0}}>
                  <MonthNavigator/>
                </Col>
                <Col style={{paddingLeft: 0, paddingRight: 0}}>
                  <div className="p-2 d-flex gap-3 flex-wrap"
                       style={{float: "right"}}>
                    <ProtectedContent allowedRoles={["Admin", "Moderator"]}>
                      <Button
                        variant={openFilters ? "primary" : "outline-primary"}
                        onClick={() => setOpenFilters(!openFilters)}
                        aria-controls="costs-filters-area"
                        aria-expanded={openFilters}
                      >

                        <FontAwesomeIcon icon="filter" className="me-2"/>
                        {translations.filters}
                      </Button>
                    </ProtectedContent>
                    <ProtectedContent allowedActions={["export_cost"]}>
                      <Button variant="outline-primary" onClick={onExportClick}>
                        <FontAwesomeIcon icon="file-export" className="me-2"/>
                        {translations.export}
                      </Button>
                    </ProtectedContent>
                    <ProtectedContent allowedActions={["create_cost"]}>
                      <Button variant="primary" onClick={onAddClick}>
                        <FontAwesomeIcon icon="add" className="me-2"/>
                        {translations.add}
                      </Button>
                    </ProtectedContent>
                  </div>
                </Col>
              </Row>
            </Container>
          </Card.Header>
          <Card.Body>
            <Collapse in={openFilters}>
              <div id="costs-filters-area">
                <Card className="mb-4">
                  <Card.Header>{translations.searchCosts}</Card.Header>
                  <Card.Body>
                    <Form onSubmit={onSearch} id="searchCostsForm">
                      <div className="mb-3">
                        <DateRangeField
                          name="inputDateRange"
                          label={translations.date}
                          placeholder={translations.enterDate}
                          error={formErrors.date}
                          startDate={dateFrom}
                          endDate={dateTo}
                          fromFieldRef={dateFromField}
                          toFieldRef={dateToField}
                        />
                      </div>
                      <div className="mb-3">
                        <SelectField
                          name="costType"
                          label={translations.type}
                          placeholder={translations.enterType}
                          options={costTypeOptions}
                          selectedOption={selectedCostTypeOption}
                          handleChange={handleCostTypeOptionChange}
                          error={formErrors.type}
                          fieldRef={typeField}
                        />
                      </div>
                      <div className="mb-3">
                        <SelectField
                          name="costCategory"
                          label={translations.category}
                          placeholder={translations.enterCategory}
                          options={filteredCostCategoryOptions}
                          selectedOption={selectedCostCategoryOption}
                          handleChange={handleCostCategoryOptionChange}
                          error={formErrors.category}
                          fieldRef={categoryField}
                        />
                      </div>
                      <div className="d-flex flex-row-reverse">
                        <Button
                          variant="primary"
                          type="submit"
                          form="searchCostsForm"
                        >
                          <FontAwesomeIcon icon="magnifying-glass"
                                           className="me-2"/>
                          {translations.search}
                        </Button>
                        <Button
                          variant="outline-primary"
                          type="button"
                          className="me-2"
                          onClick={onClearSearch}
                        >
                          <FontAwesomeIcon icon="xmark" className="me-2"/>
                          {translations.resetSearch}
                        </Button>
                      </div>
                    </Form>
                  </Card.Body>
                </Card>
              </div>
            </Collapse>
            <Card>
              <Container fluid style={{paddingLeft: 0, paddingRight: 0}}>
                <Card.Header>
                  <Row>
                    <Col
                      className="d-flex justify-content-start">
                      {StringUtils.format(
                        translations.costsSearchResults,
                        rows.length,
                        DatesUtils.format(dateFrom),
                        DatesUtils.format(dateTo)
                      )}
                    </Col>
                    <Col sm="auto" style={{height: "1rem"}}></Col>
                    <Col sm="auto"
                         className="d-flex justify-content-start">
                  <span>
                  {StringUtils.format(translations.total, totalCosts)}
                </span>
                    </Col>
                  </Row>
                </Card.Header>
              </Container>
              <Card.Body>
                {isLoading ? <Spinner/> : <SimpleDataTable
                  columns={columns}
                  rows={rows}
                  editAction={onEditClick}
                  deleteAction={onDeleteClick}/>}
              </Card.Body>
            </Card>
          </Card.Body>
        </Card>
      </Container>
      {
        openCostModal && <CostModal data={selectedRow}
                                    showModal={openCostModal}
                                    onModalSubmit={onModalSubmit}
                                    onModalClose={onModalClose}/>
      }
      {
        openConfirmationModal &&
        <ConfirmationModal data={selectedRow}
                           onConfirmationModalSubmit={onConfirmationModalSubmit}
                           onConfirmationModalClose={onConfirmationModalClose}
                           showModal={openConfirmationModal}/>
      }
    </DefaultLayout>
  )
    ;
}
